import React, { useCallback, useEffect, useState } from "react";
import type {
  EnquiriesView,
  EnquiryChannel,
  EnquiryDay,
  EnquiryInsufficientReason,
  EnquiryMeasurementView,
  EnquiryNotifyStatus,
  EnquiryStatus,
  EnquiryTotals,
  EnquiryView,
  SiteId,
} from "@siteagain/shared-types";
import { ENQUIRY_STATUSES } from "@siteagain/shared-types";

import { intlLocale, useLocale, type Locale } from "./i18n";
import { formatDateTime, formatDay, operatorFetch, operatorPost, relativeTime } from "./operator/format";
import "./enquiries.css";

/**
 * Who got in touch through the contact form, in the customer's Growth view (ADR-0028).
 *
 * A static site has no server, so its form posts to SiteAgain, which keeps the message for the owner,
 * emails it, and counts it. Counting starts with the first real enquiry, so a quiet month before that is
 * never shown as "zero enquiries". Results after a change are counts, shown with how few they usually are,
 * and never said to be caused by SiteAgain.
 */
const EN = {
  eyebrow: "Enquiries",
  title: "Who gets in touch",
  intro: "Messages sent through your website's contact form, kept for you, emailed to you, and counted so you can see what changed after an update.",
  unavailable: "Enquiries are not available yet. They will appear here as soon as they are switched on.",
  setupTitle: "Get your contact form's messages here",
  setupBody: "Your website has no server of its own, so its form needs somewhere to send messages. Point it at SiteAgain and every message arrives in your inbox here and by email, with the page it came from.",
  setupPoints: ["Nothing is shown to visitors except a thank-you", "Spam is filtered before you see it", "You can erase any message, or all of them, at any time"],
  connectForMe: "Connect my contact form",
  connectBlocked: "Another change is in progress. Finish it first, then connect the form.",
  connectHint: "SiteAgain prepares the change. You preview it and approve it before it goes live.",
  doItYourself: "Connect it yourself",
  yourEndpoint: "Your form's address",
  formExample: "A form that sends here",
  scriptTitle: "Optional: know where visitors came from",
  scriptBody: "This small script, added once to your pages, tells the form which page the visit began on and whether it came from a search engine. It only runs when someone sends the form, and stores nothing after the tab closes. Without it, enquiries are counted but their source is shown as \"not tracked\".",
  copy: "Copy",
  copied: "Copied",
  testButton: "Send a test enquiry",
  testSent: "Test enquiry sent. It appears below and an email is on its way.",
  testNote: "Counting starts with your first real enquiry. A test never counts.",
  cannotNotify: "SiteAgain has no verified email address for your account, so it cannot email you about new enquiries. They still arrive here.",
  waiting: "Waiting for the first enquiry. Once your form is connected, send a test to check the path.",
  counting: (since: string) => `Counting since ${since}`,
  windowLabel: (from: string, to: string) => `${from} – ${to}`,
  tiles: { total: "Enquiries", qualified: "Qualified", won: "Became customers", search: "From search" },
  tileHint: {
    total: "People who got in touch, spam and tests excluded",
    qualified: "Enquiries you marked as real prospects, including customers",
    won: "Enquiries you marked as won",
    search: "Enquiries whose visit began on a search engine",
  },
  versus: (days: number) => `vs the previous ${days} days`,
  noComparison: "No earlier period to compare yet",
  notTracked: "Not tracked",
  notTrackedHint: "Add the small script to see where visitors came from",
  chartTitle: "Enquiries per day",
  chartLabel: (from: string, to: string, total: string) => `Enquiries per day from ${from} to ${to}, ${total} in total`,
  peak: (count: string) => `most in a day: ${count}`,
  pagesTitle: "Where visitors came in",
  channelsTitle: "How they found you",
  channel: { search: "Search", social: "Social", referral: "Other site", direct: "Direct", unknown: "Not tracked" } satisfies Record<EnquiryChannel, string>,
  enquiryUnit: (count: string, n: number) => `${count} ${n === 1 ? "enquiry" : "enquiries"}`,
  inboxTitle: "Inbox",
  filterAll: "All",
  status: { new: "New", qualified: "Qualified", won: "Won", lost: "Lost", spam: "Spam" } satisfies Record<EnquiryStatus, string>,
  statusLabel: "Status",
  test: "Test",
  anonymous: "Someone",
  noEnquiries: "Nothing here yet.",
  noneWithStatus: "No enquiry has this status.",
  page: "Sent from",
  enteredOn: "Came in on",
  more: "Show older",
  erasedOn: (when: string) => `Personal data erased on ${when}`,
  erase: "Erase",
  eraseOne: "Erase this person's data? The message is deleted; the enquiry stays in your counts as an anonymous entry.",
  eraseAll: "Erase all personal data",
  eraseAllConfirm: "Erase the name, email, phone and message of every enquiry? The counts and comparisons stay, but the messages cannot be recovered.",
  eraseNow: "Erase",
  cancel: "Cancel",
  export: "Export as spreadsheet",
  emailState: {
    sent: "Emailed to you",
    pending: "Email on its way",
    skipped: "Not emailed",
    failed: "Email failed",
  } satisfies Record<EnquiryNotifyStatus, string>,
  resultsTitle: "What happened after our changes",
  resultsIntro: "How many people got in touch in the 28 days before and the 28 days after a change went live. A small site receives few enquiries, so most results say there is not enough to tell. This shows what moved, not why.",
  after: "28 days after",
  verdict: { improved: "More enquiries than before", neutral: "About the same", declined: "Fewer enquiries than before", insufficient: "Not enough to say" },
  moved: (before: string, after: string, percent: string | null) => `Enquiries ${before} → ${after}${percent ? ` (${percent})` : ""}`,
  searchMoved: (delta: string) => `from search ${delta}`,
  confidence: { high: "High confidence", medium: "Medium confidence", low: "Low confidence", insufficient: "" },
  overlap: (count: number) => `${count} other ${count === 1 ? "change" : "changes"} went live in this period`,
  reason: {
    baseline_incomplete: "Enquiries were not being counted for the whole period before the change.",
    window_incomplete: "The 28 days after the change had not finished yet.",
    too_few_enquiries: "Fewer than 8 enquiries in the two periods together: too few to compare.",
  } satisfies Record<EnquiryInsufficientReason, string>,
  noResults: "Results appear here 28 days after a change goes live, once enquiries have been counted for at least 28 days before it.",
  caveat: "These are counts, not proof. Season, advertising, word of mouth and prices also change how many people get in touch.",
  loadFailed: "Could not load your enquiries.",
  actionFailed: "That did not work. Please try again.",
  close: "Dismiss",
  // What the assistant is asked to do when the owner asks SiteAgain to connect the form.
  request: (endpoint: string) => [
    "Connect the contact form on my website to SiteAgain so that enquiries reach me.",
    `Make the contact form submit with method POST to ${endpoint}.`,
    "Keep every existing field, label and the visible design exactly as they are.",
    "Add a hidden field named _page whose value is the path of the page the form is on, and a hidden text field named _hp that people cannot see or reach with the keyboard (aria-hidden, tabindex -1, autocomplete off, positioned off-screen): it is a spam trap and must stay empty.",
    "If the site has a thank-you page, add a hidden field named _next with that page's full address.",
    "Do not change anything else.",
  ].join(" "),
};

const FR: typeof EN = {
  eyebrow: "Demandes",
  title: "Qui vous contacte",
  intro: "Les messages envoyés par le formulaire de contact de votre site : conservés pour vous, envoyés par e-mail, et comptés pour que vous voyiez ce qui change après une mise à jour.",
  unavailable: "Les demandes ne sont pas encore disponibles. Elles apparaîtront ici dès qu'elles seront activées.",
  setupTitle: "Recevez ici les messages de votre formulaire de contact",
  setupBody: "Votre site n'a pas de serveur : son formulaire doit envoyer ses messages quelque part. Reliez-le à SiteAgain et chaque message arrive dans votre boîte ici et par e-mail, avec la page d'où il vient.",
  setupPoints: ["Les visiteurs ne voient qu'un message de remerciement", "Le spam est filtré avant que vous le voyiez", "Vous pouvez effacer un message, ou tous, à tout moment"],
  connectForMe: "Connecter mon formulaire de contact",
  connectBlocked: "Une autre modification est en cours. Terminez-la d'abord, puis connectez le formulaire.",
  connectHint: "SiteAgain prépare la modification. Vous la prévisualisez et la validez avant sa mise en ligne.",
  doItYourself: "Le connecter vous-même",
  yourEndpoint: "L'adresse de votre formulaire",
  formExample: "Un formulaire qui envoie ici",
  scriptTitle: "Facultatif : savoir d'où viennent les visiteurs",
  scriptBody: "Ce petit script, ajouté une fois à vos pages, indique au formulaire sur quelle page la visite a commencé et si elle vient d'un moteur de recherche. Il ne s'exécute que lorsque quelqu'un envoie le formulaire, et ne conserve rien après la fermeture de l'onglet. Sans lui, les demandes sont comptées mais leur origine apparaît comme « non suivie ».",
  copy: "Copier",
  copied: "Copié",
  testButton: "Envoyer une demande de test",
  testSent: "Demande de test envoyée. Elle apparaît ci-dessous et un e-mail est en route.",
  testNote: "Le comptage commence à votre première vraie demande. Un test ne compte jamais.",
  cannotNotify: "SiteAgain n'a pas d'adresse e-mail vérifiée pour votre compte : impossible de vous prévenir par e-mail. Les demandes arrivent quand même ici.",
  waiting: "En attente de la première demande. Une fois votre formulaire connecté, envoyez un test pour vérifier le trajet.",
  counting: (since) => `Comptage depuis le ${since}`,
  windowLabel: (from, to) => `${from} – ${to}`,
  tiles: { total: "Demandes", qualified: "Qualifiées", won: "Devenues clientes", search: "Via la recherche" },
  tileHint: {
    total: "Personnes qui vous ont contacté, hors spam et tests",
    qualified: "Demandes que vous avez marquées comme de vrais prospects, clients compris",
    won: "Demandes que vous avez marquées comme gagnées",
    search: "Demandes dont la visite a commencé sur un moteur de recherche",
  },
  versus: (days) => `par rapport aux ${days} jours précédents`,
  noComparison: "Pas encore de période antérieure à comparer",
  notTracked: "Non suivi",
  notTrackedHint: "Ajoutez le petit script pour voir d'où viennent les visiteurs",
  chartTitle: "Demandes par jour",
  chartLabel: (from, to, total) => `Demandes par jour du ${from} au ${to}, ${total} au total`,
  peak: (count) => `maximum en un jour : ${count}`,
  pagesTitle: "Par où les visiteurs sont arrivés",
  channelsTitle: "Comment ils vous ont trouvé",
  channel: { search: "Recherche", social: "Réseaux sociaux", referral: "Autre site", direct: "Direct", unknown: "Non suivi" },
  enquiryUnit: (count, n) => `${count} demande${n === 1 ? "" : "s"}`,
  inboxTitle: "Boîte de réception",
  filterAll: "Toutes",
  status: { new: "Nouvelle", qualified: "Qualifiée", won: "Gagnée", lost: "Perdue", spam: "Spam" },
  statusLabel: "Statut",
  test: "Test",
  anonymous: "Quelqu'un",
  noEnquiries: "Rien ici pour le moment.",
  noneWithStatus: "Aucune demande n'a ce statut.",
  page: "Envoyé depuis",
  enteredOn: "Arrivé sur",
  more: "Afficher les plus anciennes",
  erasedOn: (when) => `Données personnelles effacées le ${when}`,
  erase: "Effacer",
  eraseOne: "Effacer les données de cette personne ? Le message est supprimé ; la demande reste dans vos totaux comme entrée anonyme.",
  eraseAll: "Effacer toutes les données personnelles",
  eraseAllConfirm: "Effacer le nom, l'e-mail, le téléphone et le message de chaque demande ? Les totaux et comparaisons restent, mais les messages ne pourront pas être récupérés.",
  eraseNow: "Effacer",
  cancel: "Annuler",
  export: "Exporter en tableur",
  emailState: {
    sent: "Envoyée par e-mail",
    pending: "E-mail en route",
    skipped: "Pas d'e-mail",
    failed: "E-mail échoué",
  },
  resultsTitle: "Ce qui s'est passé après nos modifications",
  resultsIntro: "Combien de personnes vous ont contacté pendant les 28 jours avant et les 28 jours après la mise en ligne d'une modification. Un petit site reçoit peu de demandes : la plupart des résultats disent donc qu'il n'y en a pas assez pour conclure. Cela montre ce qui a bougé, pas pourquoi.",
  after: "28 jours après",
  verdict: { improved: "Plus de demandes qu'avant", neutral: "À peu près pareil", declined: "Moins de demandes qu'avant", insufficient: "Pas assez pour conclure" },
  moved: (before, after, percent) => `Demandes ${before} → ${after}${percent ? ` (${percent})` : ""}`,
  searchMoved: (delta) => `via la recherche ${delta}`,
  confidence: { high: "Confiance élevée", medium: "Confiance moyenne", low: "Confiance faible", insufficient: "" },
  overlap: (count) => `${count} autre${count === 1 ? "" : "s"} modification${count === 1 ? "" : "s"} en ligne pendant cette période`,
  reason: {
    baseline_incomplete: "Les demandes n'étaient pas comptées pendant toute la période avant la modification.",
    window_incomplete: "Les 28 jours après la modification n'étaient pas encore terminés.",
    too_few_enquiries: "Moins de 8 demandes sur les deux périodes réunies : trop peu pour comparer.",
  },
  noResults: "Les résultats apparaissent ici 28 jours après la mise en ligne d'une modification, une fois les demandes comptées pendant au moins 28 jours avant.",
  caveat: "Ce sont des comptages, pas des preuves. La saison, la publicité, le bouche-à-oreille et les prix font aussi varier le nombre de contacts.",
  loadFailed: "Impossible de charger vos demandes.",
  actionFailed: "Cela n'a pas fonctionné. Veuillez réessayer.",
  close: "Fermer",
  request: (endpoint) => [
    "Connecte le formulaire de contact de mon site à SiteAgain pour que les demandes me parviennent.",
    `Fais envoyer le formulaire de contact en méthode POST vers ${endpoint}.`,
    "Conserve exactement tous les champs existants, les libellés et le design visible.",
    "Ajoute un champ caché nommé _page dont la valeur est le chemin de la page du formulaire, et un champ texte caché nommé _hp que les personnes ne voient pas et ne peuvent pas atteindre au clavier (aria-hidden, tabindex -1, autocomplete off, positionné hors de l'écran) : c'est un piège à spam et il doit rester vide.",
    "Si le site a une page de remerciement, ajoute un champ caché nommé _next avec l'adresse complète de cette page.",
    "Ne modifie rien d'autre.",
  ].join(" "),
};

const copyFor = (locale: Locale) => (locale === "fr" ? FR : EN);
type Copy = typeof EN;

const STASH_KEY = "siteagain_enquiries_site";
const SITE_ID = /^site_[A-Za-z0-9_-]{1,80}$/u;

/** Reads `?enquiries=site_…` (the link in the "someone got in touch" email) once, and keeps it for the tab. */
export function adoptEnquiriesLink(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("enquiries");
    if (params.has("enquiries")) {
      params.delete("enquiries");
      const query = params.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
      if (fromUrl && SITE_ID.test(fromUrl)) {
        try { sessionStorage.setItem(STASH_KEY, fromUrl); } catch {}
        return fromUrl;
      }
    }
  } catch {}
  return peekEnquiriesLink();
}

export function peekEnquiriesLink(): string | null {
  try {
    const stored = sessionStorage.getItem(STASH_KEY);
    return stored && SITE_ID.test(stored) ? stored : null;
  } catch {
    return null;
  }
}

function consumeEnquiriesLink(siteId: string): void {
  try {
    if (sessionStorage.getItem(STASH_KEY) === siteId) sessionStorage.removeItem(STASH_KEY);
  } catch {}
}

/** The small script that tells the form which page the visit began on. Shown to be copied, never installed silently. */
export const ATTRIBUTION_SCRIPT = `<script>
(function () {
  try {
    var KEY = "sa_visit", store = window.sessionStorage;
    if (!store.getItem(KEY)) {
      var q = new URLSearchParams(location.search);
      store.setItem(KEY, JSON.stringify({
        landing: location.pathname, referrer: document.referrer,
        source: q.get("utm_source") || "", medium: q.get("utm_medium") || ""
      }));
    }
    var visit = JSON.parse(store.getItem(KEY));
    document.addEventListener("submit", function (event) {
      var form = event.target;
      if (!form || !form.getAttribute || (form.getAttribute("action") || "").indexOf("/api/f/") === -1) return;
      [["_landing", visit.landing], ["_referrer", visit.referrer], ["_utm_source", visit.source], ["_utm_medium", visit.medium], ["_page", location.pathname]]
        .forEach(function (pair) {
          var input = form.querySelector('input[name="' + pair[0] + '"]');
          if (!input) { input = document.createElement("input"); input.type = "hidden"; input.name = pair[0]; form.appendChild(input); }
          input.value = pair[1] || "";
        });
    }, true);
  } catch (e) {}
})();
</script>`;

export function formExample(endpoint: string): string {
  return `<form method="post" action="${endpoint}">
  <input type="text" name="name" required>
  <input type="email" name="email" required>
  <textarea name="message" required></textarea>
  <!-- Spam trap: people never see it, bots fill it. Keep it empty. -->
  <input type="text" name="_hp" tabindex="-1" autocomplete="off" aria-hidden="true" style="position:absolute;left:-9999px">
  <input type="hidden" name="_next" value="https://your-site.example/thanks">
  <button type="submit">Send</button>
</form>`;
}

// ---------------------------------------------------------------------------

export function EnquiriesSection({
  siteId,
  onRequestChange,
  changeBlocked = false,
}: {
  siteId: SiteId;
  /** Asks the assistant to prepare a change (ADR-0028: connecting the form is an ordinary, approved change). */
  onRequestChange?: ((requestText: string) => void) | undefined;
  changeBlocked?: boolean;
}) {
  const locale = useLocale();
  const t = copyFor(locale);
  const [view, setView] = useState<EnquiriesView | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<{ tone: "good" | "bad"; text: string } | null>(null);
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState<EnquiryStatus | "all">("all");
  const [confirming, setConfirming] = useState<string | null>(null);
  const [extra, setExtra] = useState<EnquiryView[]>([]);
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async () => {
    try {
      const query = filter === "all" ? "" : `?status=${filter}`;
      const response = await operatorFetch<{ enquiries: EnquiriesView }>(`/sites/${siteId}/enquiries${query}`);
      setView(response.enquiries);
      setExtra([]);
      setHasMore(response.enquiries.hasMore);
      setError("");
    } catch {
      setError(t.loadFailed);
    } finally {
      setLoaded(true);
    }
  }, [siteId, filter, t.loadFailed]);

  useEffect(() => {
    setLoaded(false);
    void load();
  }, [load]);

  // Arriving from the email link: this section has now been seen.
  useEffect(() => { consumeEnquiriesLink(siteId); }, [siteId]);

  async function run(key: string, task: () => Promise<void>) {
    setBusy(key);
    setNotice(null);
    try {
      await task();
    } catch (reason) {
      setNotice({ tone: "bad", text: reason instanceof Error && reason.message ? reason.message : t.actionFailed });
    } finally {
      setBusy("");
    }
  }

  const sendTest = () => run("test", async () => {
    await operatorPost(`/sites/${siteId}/enquiries/test`);
    setNotice({ tone: "good", text: t.testSent });
    await load();
  });
  const setStatus = (id: string, status: EnquiryStatus) => run(`status:${id}`, async () => {
    await operatorFetch(`/sites/${siteId}/enquiries/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    await load();
  });
  const erase = (id: string | "all") => run(`erase:${id}`, async () => {
    await operatorFetch(id === "all" ? `/sites/${siteId}/enquiries` : `/sites/${siteId}/enquiries/${id}`, { method: "DELETE" });
    setConfirming(null);
    await load();
  });
  const showMore = () => run("more", async () => {
    const all = [...(view?.enquiries ?? []), ...extra];
    const last = all.at(-1);
    if (!last) return;
    const query = new URLSearchParams({ before: last.receivedAt });
    if (filter !== "all") query.set("status", filter);
    const response = await operatorFetch<{ enquiries: EnquiriesView }>(`/sites/${siteId}/enquiries?${query}`);
    setExtra([...extra, ...response.enquiries.enquiries]);
    setHasMore(response.enquiries.hasMore);
  });

  const items = view ? [...view.enquiries, ...extra] : [];
  const listening = view?.collectingSince != null;

  return (
    <section className="growth-section enq" aria-labelledby="enq-title">
      <div className="growth-section-heading">
        <div><small>{t.eyebrow}</small><h2 id="enq-title">{t.title}</h2></div>
      </div>
      <p className="enq-intro">{t.intro}</p>

      {notice && (
        <p className={`enq-notice ${notice.tone}`} role={notice.tone === "bad" ? "alert" : "status"}>
          <span>{notice.text}</span>
          <button type="button" aria-label={t.close} onClick={() => setNotice(null)}>×</button>
        </p>
      )}
      {error && <p className="enq-notice bad" role="alert">{error}</p>}
      {!loaded && !error && <div className="enq-skeleton" aria-hidden="true"><i /><i /><i /></div>}

      {loaded && view && view.endpointUrl === null && <p className="enq-empty">{t.unavailable}</p>}

      {loaded && view && view.endpointUrl !== null && (
        <>
          {!view.canNotify && <p className="enq-warning" role="status">{t.cannotNotify}</p>}

          {!listening && (
            <Setup
              endpoint={view.endpointUrl}
              copy={t}
              busy={busy}
              changeBlocked={changeBlocked}
              canRequest={onRequestChange !== undefined}
              onConnect={() => onRequestChange?.(t.request(view.endpointUrl as string))}
              onTest={() => void sendTest()}
            />
          )}

          {listening && view.totals && view.window && (
            <Summary view={view} totals={view.totals} window={view.window} copy={t} locale={locale} />
          )}

          {(listening || items.length > 0) && (
            <Inbox
              items={items}
              filter={filter}
              onFilter={setFilter}
              counts={view.statusCounts}
              listening={listening}
              copy={t}
              locale={locale}
              busy={busy}
              confirming={confirming}
              onConfirm={setConfirming}
              onStatus={(id, status) => void setStatus(id, status)}
              onErase={(id) => void erase(id)}
              hasMore={hasMore}
              onMore={() => void showMore()}
            />
          )}

          {listening && (
            <div className="enq-results">
              <h3>{t.resultsTitle}</h3>
              <p className="enq-intro">{t.resultsIntro}</p>
              {view.measurements.length === 0 ? (
                <p className="enq-empty">{t.noResults}</p>
              ) : (
                <ul>{view.measurements.map((item) => <Measurement key={item.id} item={item} copy={t} locale={locale} />)}</ul>
              )}
              <p className="enq-caveat">{t.caveat}</p>
            </div>
          )}

          {listening && (
            <footer className="enq-footer">
              <span>{t.counting(formatDay(view.collectingSince as string, locale))}</span>
              <div className="enq-footer-actions">
                <button type="button" className="enq-quiet" onClick={() => void sendTest()} disabled={busy === "test"}>{t.testButton}</button>
                <a className="enq-quiet" href={`/api/sites/${siteId}/enquiries/export`} download>{t.export}</a>
                {confirming === "all" ? (
                  <div className="enq-confirm" role="alert">
                    <p>{t.eraseAllConfirm}</p>
                    <div>
                      <button type="button" className="enq-danger" onClick={() => void erase("all")} disabled={busy === "erase:all"}>{t.eraseNow}</button>
                      <button type="button" onClick={() => setConfirming(null)}>{t.cancel}</button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="enq-quiet danger" onClick={() => setConfirming("all")}>{t.eraseAll}</button>
                )}
              </div>
            </footer>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function CopyBlock({ label, text, copy }: { label: string; text: string; copy: Copy }) {
  const [copied, setCopied] = useState(false);
  async function copyText() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      // The text is selectable; copying by hand still works.
    }
  }
  return (
    <div className="enq-code">
      <div><span>{label}</span><button type="button" onClick={() => void copyText()}>{copied ? copy.copied : copy.copy}</button></div>
      <pre tabIndex={0}><code>{text}</code></pre>
    </div>
  );
}

function Setup({
  endpoint,
  copy,
  busy,
  changeBlocked,
  canRequest,
  onConnect,
  onTest,
}: {
  endpoint: string;
  copy: Copy;
  busy: string;
  changeBlocked: boolean;
  canRequest: boolean;
  onConnect(): void;
  onTest(): void;
}) {
  return (
    <div className="enq-setup">
      <h3>{copy.setupTitle}</h3>
      <p>{copy.setupBody}</p>
      <ul>{copy.setupPoints.map((line) => <li key={line}>{line}</li>)}</ul>
      <div className="enq-actions">
        {canRequest && (
          <div>
            <button type="button" className="enq-primary" onClick={onConnect} disabled={changeBlocked}>{copy.connectForMe}</button>
            <small>{changeBlocked ? copy.connectBlocked : copy.connectHint}</small>
          </div>
        )}
        <div>
          <button type="button" className="enq-secondary" onClick={onTest} disabled={busy === "test"}>{copy.testButton}</button>
          <small>{copy.testNote}</small>
        </div>
      </div>
      <details className="enq-diy">
        <summary>{copy.doItYourself}</summary>
        <CopyBlock label={copy.yourEndpoint} text={endpoint} copy={copy} />
        <CopyBlock label={copy.formExample} text={formExample(endpoint)} copy={copy} />
        <h4>{copy.scriptTitle}</h4>
        <p>{copy.scriptBody}</p>
        <CopyBlock label="<script>" text={ATTRIBUTION_SCRIPT} copy={copy} />
      </details>
      <p className="enq-waiting" role="status">{copy.waiting}</p>
    </div>
  );
}

function Summary({
  view,
  totals,
  window,
  copy,
  locale,
}: {
  view: EnquiriesView;
  totals: EnquiryTotals;
  window: { from: string; to: string; days: number };
  copy: Copy;
  locale: Locale;
}) {
  const number = new Intl.NumberFormat(intlLocale(locale));
  const previous = view.previousTotals;
  const change = previous && previous.total > 0 ? ((totals.total - previous.total) / previous.total) * 100 : null;
  const direct = previous ? totals.total - previous.total : null;
  const versus = previous ? copy.versus(window.days) : copy.noComparison;
  // A search figure is shown only when most enquiries say where they came from.
  const searchKnown = totals.total > 0 && totals.attributed * 2 >= totals.total;
  const peak = Math.max(1, ...view.series.map((day) => day.count));
  return (
    <>
      <p className="enq-window">{copy.windowLabel(formatDay(window.from, locale), formatDay(window.to, locale))}</p>
      <div className="enq-tiles">
        <Tile
          label={copy.tiles.total}
          hint={copy.tileHint.total}
          value={number.format(totals.total)}
          change={change === null ? (direct === null || direct === 0 ? null : `${direct > 0 ? "+" : ""}${number.format(direct)}`) : percentChip(change, locale)}
          moved={direct === null || direct === 0 ? "flat" : direct > 0 ? "up" : "down"}
          note={versus}
        />
        <Tile label={copy.tiles.qualified} hint={copy.tileHint.qualified} value={number.format(totals.qualified)} note={copy.tileHint.qualified} />
        <Tile label={copy.tiles.won} hint={copy.tileHint.won} value={number.format(totals.won)} note={copy.tileHint.won} />
        <Tile
          label={copy.tiles.search}
          hint={copy.tileHint.search}
          value={searchKnown ? number.format(totals.fromSearch) : "—"}
          note={searchKnown ? copy.tileHint.search : copy.notTrackedHint}
        />
      </div>

      <Bars days={view.series} copy={copy} locale={locale} from={view.series[0]?.date ?? window.from} to={window.to} total={totals.total} peak={peak} />

      <div className="enq-lists">
        <section className="enq-ranked">
          <h3>{copy.pagesTitle}</h3>
          {view.byPage.length === 0 ? <p className="enq-empty">—</p> : (
            <ol>{view.byPage.slice(0, 5).map((row) => (
              <li key={row.key}><span title={row.key}>{row.key}</span><strong>{copy.enquiryUnit(number.format(row.count), row.count)}</strong></li>
            ))}</ol>
          )}
        </section>
        <section className="enq-ranked">
          <h3>{copy.channelsTitle}</h3>
          {view.byChannel.length === 0 ? <p className="enq-empty">—</p> : (
            <ol>{view.byChannel.map((row) => (
              <li key={row.key}><span>{copy.channel[row.key as EnquiryChannel] ?? row.key}</span><strong>{number.format(row.count)}</strong></li>
            ))}</ol>
          )}
        </section>
      </div>
    </>
  );
}

function Tile({ label, hint, value, change, moved = "flat", note }: { label: string; hint: string; value: string; change?: string | null; moved?: "up" | "down" | "flat"; note: string }) {
  return (
    <article className="enq-tile" title={hint}>
      <small>{label}</small>
      <strong>{value}</strong>
      <p className={`enq-change ${moved}`}>
        {change ? <><b aria-hidden="true">{moved === "up" ? "▲" : moved === "down" ? "▼" : "•"}</b> {change}</> : <span>&nbsp;</span>}
      </p>
      <em>{note}</em>
    </article>
  );
}

function Bars({ days, copy, locale, from, to, total, peak }: { days: EnquiryDay[]; copy: Copy; locale: Locale; from: string; to: string; total: number; peak: number }) {
  if (days.length === 0) return null;
  const width = 640;
  const height = 96;
  const step = width / days.length;
  const number = new Intl.NumberFormat(intlLocale(locale));
  const label = copy.chartLabel(formatDay(from, locale), formatDay(to, locale), number.format(total));
  return (
    <figure className="enq-chart">
      <figcaption><span>{copy.chartTitle}</span><span>{copy.peak(number.format(peak))}</span></figcaption>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={label}>
        <title>{label}</title>
        <line x1="0" x2={width} y1={height - 1} y2={height - 1} className="enq-baseline" vectorEffect="non-scaling-stroke" />
        {days.map((day, index) => {
          if (day.count === 0) return null;
          const barHeight = Math.max(4, (day.count / peak) * (height - 14));
          return <rect key={day.date} x={index * step + step * 0.15} y={height - 1 - barHeight} width={Math.max(2, step * 0.7)} height={barHeight} rx="1.5" className="enq-bar" />;
        })}
      </svg>
      <div className="enq-axis"><span>{formatDay(from, locale)}</span><span>{formatDay(to, locale)}</span></div>
    </figure>
  );
}

function Inbox({
  items,
  filter,
  onFilter,
  counts,
  listening,
  copy,
  locale,
  busy,
  confirming,
  onConfirm,
  onStatus,
  onErase,
  hasMore,
  onMore,
}: {
  items: EnquiryView[];
  filter: EnquiryStatus | "all";
  onFilter(filter: EnquiryStatus | "all"): void;
  counts: Record<EnquiryStatus, number>;
  listening: boolean;
  copy: Copy;
  locale: Locale;
  busy: string;
  confirming: string | null;
  onConfirm(id: string | null): void;
  onStatus(id: string, status: EnquiryStatus): void;
  onErase(id: string): void;
  hasMore: boolean;
  onMore(): void;
}) {
  return (
    <section className="enq-inbox" aria-labelledby="enq-inbox-title">
      <div className="enq-inbox-head">
        <h3 id="enq-inbox-title">{copy.inboxTitle}</h3>
        {listening && (
          <div className="enq-filters" role="group" aria-label={copy.statusLabel}>
            {(["all", ...ENQUIRY_STATUSES] as const).map((status) => (
              <button
                key={status}
                type="button"
                aria-pressed={filter === status}
                className={filter === status ? "is-active" : ""}
                onClick={() => onFilter(status)}
              >
                {status === "all" ? copy.filterAll : copy.status[status]}
                {status !== "all" && <span>{counts[status]}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <p className="enq-empty">{filter === "all" ? copy.noEnquiries : copy.noneWithStatus}</p>
      ) : (
        <ul>
          {items.map((item) => (
            <Item
              key={item.id}
              item={item}
              copy={copy}
              locale={locale}
              busy={busy}
              confirming={confirming === item.id}
              onConfirm={onConfirm}
              onStatus={onStatus}
              onErase={onErase}
            />
          ))}
        </ul>
      )}
      {hasMore && <button type="button" className="enq-secondary enq-more" onClick={onMore} disabled={busy === "more"}>{copy.more}</button>}
    </section>
  );
}

function Item({
  item,
  copy,
  locale,
  busy,
  confirming,
  onConfirm,
  onStatus,
  onErase,
}: {
  item: EnquiryView;
  copy: Copy;
  locale: Locale;
  busy: string;
  confirming: boolean;
  onConfirm(id: string | null): void;
  onStatus(id: string, status: EnquiryStatus): void;
  onErase(id: string): void;
}) {
  const who = item.name ?? item.email ?? copy.anonymous;
  const phone = item.phone?.replace(/[^0-9+]/gu, "") ?? "";
  const fields = Object.entries(item.fields);
  return (
    <li className={`enq-item${item.status === "spam" ? " is-spam" : ""}${item.isTest ? " is-test" : ""}`}>
      <div className="enq-item-head">
        <div>
          <strong>{who}</strong>
          {item.isTest && <span className="enq-chip">{copy.test}</span>}
          <time dateTime={item.receivedAt} title={formatDateTime(item.receivedAt, locale)}>{relativeTime(item.receivedAt, locale)}</time>
        </div>
        {!item.isTest && (
          <label className="enq-status">
            <span className="enq-visually-hidden">{copy.statusLabel}</span>
            <select
              value={item.status}
              disabled={busy === `status:${item.id}`}
              onChange={(event) => onStatus(item.id, event.target.value as EnquiryStatus)}
            >
              {ENQUIRY_STATUSES.map((status) => <option key={status} value={status}>{copy.status[status]}</option>)}
            </select>
          </label>
        )}
      </div>

      {item.erasedAt ? (
        <p className="enq-erased">{copy.erasedOn(formatDay(item.erasedAt, locale))}</p>
      ) : (
        <>
          {item.message && <p className="enq-message">{item.message}</p>}
          <p className="enq-contact">
            {item.email && <a href={`mailto:${item.email}`}>{item.email}</a>}
            {phone && item.phone && <a href={`tel:${phone}`}>{item.phone}</a>}
          </p>
          {fields.length > 0 && (
            <dl className="enq-fields">
              {fields.map(([key, value]) => <React.Fragment key={key}><dt>{key}</dt><dd>{value}</dd></React.Fragment>)}
            </dl>
          )}
        </>
      )}

      <p className="enq-meta">
        {item.pagePath && <span>{copy.page} <code>{item.pagePath}</code></span>}
        {item.landingPath && item.landingPath !== item.pagePath && <span>{copy.enteredOn} <code>{item.landingPath}</code></span>}
        {item.channel !== "unknown" && <span>{copy.channel[item.channel]}{item.referrerHost && item.channel !== "direct" ? ` · ${item.referrerHost}` : ""}</span>}
        <span className={`enq-email ${item.notify.status}`} title={item.notify.detail ?? undefined}>
          {copy.emailState[item.notify.status]}{item.notify.status !== "sent" && item.notify.detail ? ` — ${item.notify.detail}` : ""}
        </span>
      </p>

      {!item.erasedAt && (
        confirming ? (
          <div className="enq-confirm" role="alert">
            <p>{copy.eraseOne}</p>
            <div>
              <button type="button" className="enq-danger" onClick={() => onErase(item.id)} disabled={busy === `erase:${item.id}`}>{copy.eraseNow}</button>
              <button type="button" onClick={() => onConfirm(null)}>{copy.cancel}</button>
            </div>
          </div>
        ) : (
          <button type="button" className="enq-quiet danger" onClick={() => onConfirm(item.id)}>{copy.erase}</button>
        )
      )}
    </li>
  );
}

function Measurement({ item, copy, locale }: { item: EnquiryMeasurementView; copy: Copy; locale: Locale }) {
  const number = new Intl.NumberFormat(intlLocale(locale));
  const percent = percentChip(item.change.percent, locale);
  const searchDelta = item.change.searchDelta;
  return (
    <li className={`enq-result verdict-${item.verdict}`}>
      <div className="enq-result-head">
        <strong>{item.changeSummary ?? "—"}</strong>
        <span>{copy.after}</span>
      </div>
      <p className="enq-result-verdict">{copy.verdict[item.verdict]}</p>
      <p className="enq-result-detail">
        {copy.moved(number.format(item.before.total), number.format(item.after.total), percent)}
        {searchDelta !== null && searchDelta !== 0 ? ` · ${copy.searchMoved(`${searchDelta > 0 ? "+" : ""}${number.format(searchDelta)}`)}` : ""}
      </p>
      {item.verdict === "insufficient" && item.reason && <p className="enq-result-detail">{copy.reason[item.reason]}</p>}
      <p className="enq-result-meta">
        {copy.confidence[item.confidence]}
        {item.overlappingChanges > 0 && <>{item.confidence !== "insufficient" ? " · " : ""}{copy.overlap(item.overlappingChanges)}</>}
      </p>
    </li>
  );
}

function percentChip(value: number | null, locale: Locale): string | null {
  if (value === null) return null;
  const formatted = new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: 0 }).format(Math.round(value));
  return `${value > 0 ? "+" : ""}${formatted}%`;
}
